import type {
  DosePer100LUnit, Orchard, SprayMachine, SprayProduct, SprayRound,
} from '../services/sprayService';

/**
 * Pure helpers for the Edit-Page Planning Summary card.
 *
 * Track: spray_session_ui_changes_20260502 / Phase 2 / Task 1.
 *
 * Each helper returns `{ value, unit }` for a complete input set or `null`
 * when any required field is missing — including 0 area / 0 waterPerHectare
 * / 0 tankVolume, all of which the spec treats as "missing" (rendered as
 * an em-dash `—`). The exception is plannedWater = 0, which propagates as
 * a real 0 value (spec: "0 planned water → 0 tanks (rendered as `0`,
 * not `—`)").
 *
 * No I/O, no rounding for display — formatters elsewhere handle that.
 */

export interface PlanningCalcResult {
  value: number;
  unit: string;
  /**
   * Optional: the unrounded numerator-over-denominator result. Populated by
   * calculateNumberOfTanks so the UI can render the exact tank requirement
   * (e.g. 2.4) alongside the ceiled count (3 Tanks). Other helpers leave
   * this undefined.
   */
  exactValue?: number;
}

/** Total Planned Water (L) = orchard.areaHectares × round.waterPerHectare. */
export function calculateTotalPlannedWater(
  orchard: Orchard | null | undefined,
  round: SprayRound | null | undefined,
): PlanningCalcResult | null {
  if (!orchard || !round) return null;
  const area = orchard.areaHectares;
  const water = round.waterPerHectare;
  if (!area || !water) return null;
  return { value: area * water, unit: 'L' };
}

/**
 * Total Product Amount (per product in `round.productList`):
 *   (plannedWater / 100) × product.dosePer100L
 * Unit propagates from the product (`ml`, `g`, `L`, `kg`) — no conversion.
 */
export function calculateTotalProductAmount(
  plannedWater: number | null | undefined,
  product: SprayProduct | null | undefined,
): PlanningCalcResult | null {
  if (plannedWater == null || !product) return null;
  const dose = product.dosePer100L;
  const unit: DosePer100LUnit | undefined = product.dosePer100LUnit;
  if (dose == null || !unit) return null;
  return { value: (plannedWater / 100) * dose, unit };
}

/**
 * Number of Tanks = Math.ceil(plannedWater / machine.tankVolume).
 * Exact divides return their natural integer (5000 / 1000 = 5, not 6).
 * plannedWater = 0 returns 0 tanks (a real 0, not null).
 *
 * Also exposes the unrounded `exactValue` so the UI can render
 * `3 Tanks (2.4 exact)` alongside the ceiled count.
 */
export function calculateNumberOfTanks(
  plannedWater: number | null | undefined,
  machine: SprayMachine | null | undefined,
): PlanningCalcResult | null {
  if (plannedWater == null || !machine) return null;
  const tank = machine.tankVolume;
  if (!tank) return null;
  const exactValue = plannedWater / tank;
  return { value: Math.ceil(exactValue), exactValue, unit: 'tanks' };
}

/**
 * Per Tank Dosage (per product) = (machine.tankVolume / 100) × product.dosePer100L.
 * Unit propagates from the product, identical to total-amount semantics.
 */
export function calculatePerTankDosage(
  machine: SprayMachine | null | undefined,
  product: SprayProduct | null | undefined,
): PlanningCalcResult | null {
  if (!machine || !product) return null;
  const tank = machine.tankVolume;
  const dose = product.dosePer100L;
  const unit: DosePer100LUnit | undefined = product.dosePer100LUnit;
  if (!tank || dose == null || !unit) return null;
  return { value: (tank / 100) * dose, unit };
}
